/* ==========================================================================
   main-product.js — Modern PDP buy-box behaviour
   --------------------------------------------------------------------------
   Owns:
     - Variant option selection → hidden id input + URL + price + gallery
     - Sold-out reachability check across other option groups
     - AJAX add-to-cart → dispatch `product:added` so Prestige's cart drawer
       opens (verified contract from theme.min.js: Prestige listens on
       document for "product:added" with bubbles:true)
     - Size guide modal open/close
     - Sticky ATC button → forwards click to the real ATC button

   Boot pattern: queries every `[data-component="main-product-form"]` in
   the DOM and wires it up. Idempotent — safe to re-run on shopify:section:load
   for theme editor.
   ========================================================================== */

(function () {
  'use strict';

  var MONEY_FORMAT = (window.theme && window.theme.moneyFormat) || '${{amount}}';

  function formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = MONEY_FORMAT;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  /* --------------------------------------------------------------------- */
  /* Variant selection + form                                              */
  /* --------------------------------------------------------------------- */

  function MainProductForm(root) {
    this.root = root;
    this.handle = root.getAttribute('data-product-handle');
    this.section = root.closest('.MainProduct') || root.parentElement;
    this.form = root.querySelector('form');
    this.idInput = root.querySelector('[data-product-variant-id]');
    this.atcButton = root.querySelector('[data-main-product-atc]');
    this.atcLabel = root.querySelector('[data-atc-label]');
    this.errorEl = root.querySelector('[data-form-error]');
    this.optionsRoot = root.querySelector('[data-component="main-product-options"]');
    this.priceRoot = this.section && this.section.querySelector('[data-component="main-product-price"]');

    var dataNode = root.querySelector('[data-main-product-variants]');
    try {
      this.variants = dataNode ? JSON.parse(dataNode.textContent) : [];
    } catch (e) {
      this.variants = [];
    }

    this.optionInputs = root.querySelectorAll('.MainProduct-Option__input');
    this.fieldsets = root.querySelectorAll('.MainProduct-Option');
    // Size starts unselected (data-size-required); see main-product-variants
    this.sizeFs = root.querySelector('[data-size-required]');
    this.bindEvents();
    this.updateAvailability();
  }

  MainProductForm.prototype.bindEvents = function () {
    var self = this;

    // Option change → re-evaluate selected variant
    this.optionInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        self.onOptionChange();
      });
    });

    // Shop Pay / dynamic checkout: no size yet → prompt instead of buying a default size
    var payment = this.root.querySelector('.MainProduct-Form__payment');
    if (payment) {
      payment.addEventListener('click', function (e) {
        if (self.sizeChosen()) return;
        e.preventDefault();
        e.stopPropagation();
        self.promptSize();
      }, true);
    }

    // Form submit → AJAX add to cart
    if (this.form) {
      this.form.addEventListener('submit', function (e) {
        self.onSubmit(e);
      });
    }
  };

  MainProductForm.prototype.getSelectedOptions = function () {
    var values = [];
    var seen = {};
    this.fieldsets.forEach(function (fs) {
      var name = fs.getAttribute('data-option-name');
      var pos = parseInt(fs.getAttribute('data-option-position'), 10);
      var checked = fs.querySelector('.MainProduct-Option__input:checked');
      if (checked && !seen[name]) {
        values.push({ name: name, position: pos, value: checked.value });
        seen[name] = true;
      }
    });
    // Sort by position so options[] order matches Shopify variant.options
    values.sort(function (a, b) { return a.position - b.position; });
    return values;
  };

  MainProductForm.prototype.findVariant = function (selectedOptions) {
    var byPos = {};
    selectedOptions.forEach(function (o) { byPos[o.position - 1] = o.value; });

    return this.variants.find(function (v) {
      for (var i = 0; i < v.options.length; i++) {
        if (byPos[i] !== undefined && v.options[i] !== byPos[i]) return false;
      }
      return true;
    });
  };

  MainProductForm.prototype.sizeChosen = function () {
    return !this.sizeFs || !!this.sizeFs.querySelector('.MainProduct-Option__input:checked');
  };

  MainProductForm.prototype.promptSize = function () {
    var fs = this.sizeFs;
    if (!fs) return;
    fs.classList.add('is-size-error');
    var label = fs.querySelector('[data-selected-value]');
    if (label) label.textContent = 'Please select a size';
    var header = document.getElementById('shopify-section-header');
    var offset = (header ? header.getBoundingClientRect().bottom : 0) + 16;
    var top = fs.getBoundingClientRect().top + window.scrollY - offset;
    if (fs.getBoundingClientRect().top < offset || fs.getBoundingClientRect().bottom > window.innerHeight) {
      window.scrollTo({ top: top, behavior: 'smooth' });
    }
    var first = fs.querySelector('.MainProduct-Option__item:not(.is-soldout) .MainProduct-Option__input');
    if (first) first.focus({ preventScroll: true });
  };

  MainProductForm.prototype.onOptionChange = function () {
    if (this.sizeFs && this.sizeChosen()) this.sizeFs.classList.remove('is-size-error');
    var selected = this.getSelectedOptions();
    var variant = this.findVariant(selected);

    this.updateLegendValues();
    this.updateAvailability();

    if (!variant) {
      // No variant matches the current selection
      this.setVariant(null);
      return;
    }

    this.setVariant(variant);
  };

  MainProductForm.prototype.updateLegendValues = function () {
    this.fieldsets.forEach(function (fs) {
      var checked = fs.querySelector('.MainProduct-Option__input:checked');
      var legendValue = fs.querySelector('[data-selected-value]');
      if (checked && legendValue) {
        legendValue.textContent = checked.value;
      }
      // Update is-selected class on items
      fs.querySelectorAll('.MainProduct-Option__item').forEach(function (item) {
        var val = item.getAttribute('data-option-value');
        item.classList.toggle('is-selected', !!checked && checked.value === val);
      });
    });
  };

  /* For each option group, mark items unreachable from the current selection
     in the OTHER groups. e.g. if Color=Tan is selected, disable any size
     whose variant in Tan is sold out. */
  MainProductForm.prototype.updateAvailability = function () {
    var self = this;
    var selected = this.getSelectedOptions();

    this.fieldsets.forEach(function (fs) {
      var optionName = fs.getAttribute('data-option-name');
      var optionPos = parseInt(fs.getAttribute('data-option-position'), 10);
      var optionIndex = optionPos - 1;

      fs.querySelectorAll('.MainProduct-Option__item').forEach(function (item) {
        var value = item.getAttribute('data-option-value');
        var input = item.querySelector('.MainProduct-Option__input');

        // For this candidate value, build a hypothetical option set:
        // keep all OTHER selected options as-is, but use this candidate value
        // for the current option group. If any variant matches and is available,
        // it's reachable. If all matching variants are unavailable, soldout.
        var anyAvailable = false;
        for (var i = 0; i < self.variants.length; i++) {
          var v = self.variants[i];
          var matches = v.options[optionIndex] === value;
          if (!matches) continue;
          var mismatch = false;
          for (var j = 0; j < selected.length; j++) {
            var sel = selected[j];
            if (sel.position === optionPos) continue; // skip the option we're testing
            if (v.options[sel.position - 1] !== sel.value) { mismatch = true; break; }
          }
          if (!mismatch && v.available) { anyAvailable = true; break; }
        }

        item.classList.toggle('is-soldout', !anyAvailable);
        if (input) {
          // We don't disable the input — let the user click a sold-out option
          // to see it greyed out elsewhere. The ATC button reflects availability.
          input.removeAttribute('disabled');
        }
      });
    });
  };

  MainProductForm.prototype.setVariant = function (variant) {
    if (variant && !this.sizeChosen()) {
      // Colour picked, size not yet: update price and gallery only. No ?variant=
      // in the URL (a reload or shared link would then preselect that size),
      // and Add to Cart stays enabled so it can prompt for a size.
      this.idInput.value = variant.id;
      this.updatePrice(variant);
      if (this.atcButton) this.atcButton.disabled = false;
      if (this.atcLabel) this.atcLabel.textContent = this.atcButton.getAttribute('data-available-text') || 'Add to Cart';
      document.dispatchEvent(new CustomEvent('main-product:variant-change', {
        detail: { variant: variant, sectionId: this.section && this.section.getAttribute('data-section-id') }
      }));
      return;
    }
    if (variant) {
      this.idInput.value = variant.id;
      this.idInput.removeAttribute('disabled');

      // Update URL
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {}

      // Update price block
      this.updatePrice(variant);

      // Update ATC label + disabled
      this.updateATC(variant);

      // Swap gallery image
      this.swapGalleryImage(variant);

      // Notify any external listeners (sticky ATC, future Fit-and-Feel, etc.)
      document.dispatchEvent(new CustomEvent('main-product:variant-change', {
        detail: { variant: variant, sectionId: this.section && this.section.getAttribute('data-section-id') }
      }));
    } else {
      // No matching variant — disable ATC
      this.idInput.value = '';
      this.idInput.setAttribute('disabled', 'disabled');
      this.atcButton.disabled = true;
      if (this.atcLabel) this.atcLabel.textContent = 'Unavailable';
    }
  };

  MainProductForm.prototype.updatePrice = function (variant) {
    if (!this.priceRoot) return;
    var current = this.priceRoot.querySelector('[data-price]');
    var compare = this.priceRoot.querySelector('[data-compare-at-price]');
    var savings = this.priceRoot.querySelector('[data-savings]');
    if (current) current.textContent = formatMoney(variant.price);

    var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (compare) {
      if (onSale) {
        compare.textContent = formatMoney(variant.compare_at_price);
        compare.removeAttribute('hidden');
      } else {
        compare.setAttribute('hidden', 'hidden');
      }
    }

    if (savings) {
      if (onSale) {
        var pct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
        savings.textContent = '−' + pct + '%';
        savings.removeAttribute('hidden');
      } else {
        savings.setAttribute('hidden', 'hidden');
      }
    }
  };

  MainProductForm.prototype.updateATC = function (variant) {
    if (!this.atcButton || !this.atcLabel) return;
    if (variant.available) {
      this.atcButton.disabled = false;
      this.atcLabel.textContent = this.atcButton.getAttribute('data-available-text') || 'Add to Cart';
    } else {
      this.atcButton.disabled = true;
      this.atcLabel.textContent = this.atcButton.getAttribute('data-soldout-text') || 'Sold out';
    }
  };

  MainProductForm.prototype.swapGalleryImage = function (variant) {
    // Legacy hook kept as a no-op: the vertical-stack gallery in
    // sections/main-product.liquid handles variant filtering via its own
    // listener on the `main-product:variant-change` event (dispatched right
    // after this fn returns in updateForVariant). Doing scroll-into-view
    // here as well caused double-scroll jitter and disrupted the sticky
    // right column on desktop. Left in place so any future caller / sub-
    // component doesn't trip a "not a function" error.
    return true;
  };

  MainProductForm.prototype.setError = function (message) {
    if (!this.errorEl) return;
    if (message) {
      this.errorEl.textContent = message;
      this.errorEl.removeAttribute('hidden');
    } else {
      this.errorEl.textContent = '';
      this.errorEl.setAttribute('hidden', 'hidden');
    }
  };

  MainProductForm.prototype.onSubmit = function (e) {
    if (!this.sizeChosen()) {
      e.preventDefault();
      this.promptSize();
      return;
    }
    if (!this.form || !window.fetch) return;
    e.preventDefault();
    this.setError(null);

    var self = this;
    var formData = new FormData(this.form);
    this.atcButton.classList.add('is-loading');
    this.atcButton.disabled = true;

    fetch(window.theme && window.theme.localeRootUrl ? window.theme.localeRootUrl + '/cart/add.js' : '/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/javascript' },
      body: formData
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) { throw err; });
        }
        return res.json();
      })
      .then(function (item) {
        // Resolve the current variant for the event detail. Find by id.
        var variant = self.variants.find(function (v) { return String(v.id) === String(item.variant_id || item.id); });
        var detail = {
          variant: variant || item,
          quantity: parseInt(formData.get('quantity') || '1', 10)
        };
        // Prestige's cart drawer listens on document for "product:added"
        // with bubbles:true. We dispatch on the form so it bubbles up.
        self.form.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: detail
        }));
      })
      .catch(function (err) {
        var msg = (err && (err.description || err.message)) || 'Could not add to cart. Please try again.';
        self.setError(msg);
      })
      .finally(function () {
        self.atcButton.classList.remove('is-loading');
        self.atcButton.disabled = false;
      });
  };

  /* --------------------------------------------------------------------- */
  /* Size guide modal: handled inline in snippets/main-product-size-modal   */
  /* --------------------------------------------------------------------- */

  /* --------------------------------------------------------------------- */
  /* Sticky ATC: forwards click to the in-view ATC button                  */
  /* --------------------------------------------------------------------- */

  function bindStickyATC(scope) {
    var sticky = scope.querySelector('[data-sticky-atc-button]');
    var realBtn = scope.querySelector('[data-main-product-atc]');
    if (!sticky || !realBtn) return;
    sticky.addEventListener('click', function (e) {
      e.preventDefault();
      realBtn.click();
    });
  }

  /* --------------------------------------------------------------------- */
  /* Boot                                                                  */
  /* --------------------------------------------------------------------- */

  function boot(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-component="main-product-form"]').forEach(function (el) {
      if (el.__mainProductInited) return;
      el.__mainProductInited = true;
      new MainProductForm(el);
    });
    scope.querySelectorAll('.MainProduct').forEach(function (el) {
      if (el.__mainProductScopeInited) return;
      el.__mainProductScopeInited = true;
      bindStickyATC(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) boot(e.target);
  });
})();
